import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { setInstalledKw } from "../features/settings/settingsSlice";

function InstalledCapacityRow() {
  const dispatch = useDispatch();
  const stored = useSelector((s) => s.settings.installedKw);
  const [val, setVal] = useState(String(stored));

  // Keep local input in sync if the stored value changes elsewhere.
  useEffect(() => { setVal(String(stored)); }, [stored]);

  const commit = () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) {
      setVal(String(stored));
      toast.error("Enter a positive number");
      return;
    }
    if (n === stored) return;
    dispatch(setInstalledKw(n));
    toast.success(`Capacity set to ${n} kW`);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.25 }}>
      <Box sx={{ color: "warning.main", display: "flex" }}>
        <BoltOutlinedIcon />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Installed Capacity
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Sets the upper limit (0 → this value) on every dashboard gauge.
        </Typography>
      </Box>
      <TextField
        type="number"
        size="small"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
        InputProps={{
          endAdornment: <InputAdornment position="end">kW</InputAdornment>,
        }}
        inputProps={{ min: 1, step: 1, style: { width: 72, textAlign: "right" } }}
      />
    </Stack>
  );
}

export default function SettingsPage() {
  const installedKw = useSelector((s) => s.settings.installedKw);
  const dispatch = useDispatch();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: "auto" }}>
      <Typography variant="h2" sx={{ fontSize: "1.25rem", fontWeight: 600, mb: 2 }}>
        Settings
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
        <InstalledCapacityRow />
        {installedKw !== 50 && (
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => {
                dispatch(setInstalledKw(50));
                toast.success("Reset to default (50 kW)");
              }}
            >
              Reset to default
            </Button>
          </Stack>
        )}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mt: 3 }}>
        v1.0.0 · © Surya Solars
      </Typography>
    </Box>
  );
}
